
import React, { useState, useRef, useEffect } from 'react';
import { motion, PanInfo, useAnimation } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserProfile } from '@/types';
import { useIsMobile } from '@/hooks/use-mobile';

// Import new component files
import SwipeCardHeader from './card/SwipeCardHeader';
import SwipeCardFooter from './card/SwipeCardFooter';
import ProfileTabContent from './card/ProfileTabContent';
import MusicTabContent from './card/MusicTabContent';
import CompatibilityTabContent from './card/CompatibilityTabContent';

interface SwipeCardProps {
  profile: UserProfile & { matchScore?: any };
  onLike: (profileId: string) => void;
  onPass: (profileId: string) => void;
}

const SwipeCard: React.FC<SwipeCardProps> = ({ profile, onLike, onPass }) => {
  const controls = useAnimation();
  const [exitDirection, setExitDirection] = useState<'left' | 'right' | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = useIsMobile();
  const [isDragging, setIsDragging] = useState(false);
  
  useEffect(() => {
    setExitDirection(null);
    controls.set({ x: 0, opacity: 1 });
    setIsAnimating(false);
  }, [profile, controls]);
  
  const handleSwipeComplete = (profileId: string, direction: 'left' | 'right') => {
    if (direction === 'right') {
      onLike(profileId);
    } else {
      onPass(profileId);
    }
  };
  
  const handleDragStart = () => {
    setIsDragging(true);
  };
  
  const handleDragEnd = async (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false);
    
    if (isAnimating) return;
    
    const threshold = 100;
    setIsAnimating(true);
    
    if (info.offset.x > threshold) {
      // Right swipe - Like
      setExitDirection('right');
      await controls.start({ x: window.innerWidth, opacity: 0, transition: { duration: 0.3 } });
      handleSwipeComplete(profile.userId, 'right');
    } else if (info.offset.x < -threshold) {
      // Left swipe - Pass
      setExitDirection('left');
      await controls.start({ x: -window.innerWidth, opacity: 0, transition: { duration: 0.3 } });
      handleSwipeComplete(profile.userId, 'left');
    } else {
      await controls.start({ x: 0, transition: { type: 'spring', stiffness: 300, damping: 20 } });
      setIsAnimating(false);
    }
  };
  
  const handleLikeClick = async () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setExitDirection('right');
    await controls.start({ x: window.innerWidth, opacity: 0, transition: { duration: 0.3 } });
    handleSwipeComplete(profile.userId, 'right');
  };
  
  const handlePassClick = async () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setExitDirection('left');
    await controls.start({ x: -window.innerWidth, opacity: 0, transition: { duration: 0.3 } });
    handleSwipeComplete(profile.userId, 'left');
  };

  return (
    <motion.div
      ref={cardRef}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      animate={controls}
      initial={{ x: 0 }}
      whileDrag={{ scale: 1.02 }}
      className={`${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
    >
      <Card className="overflow-hidden relative">
        <div className="aspect-video md:aspect-auto md:h-64 w-full">
          <img 
            src={profile.avatarUrl} 
            alt={profile.name} 
            className="w-full h-full object-cover"
          />
        </div>
        
        <SwipeCardHeader 
          name={profile.name}
          role={profile.role}
          location={profile.location}
          matchScore={profile.matchScore}
        />

        <Tabs defaultValue="profile" className="w-full">
          <div className="px-6">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="profile" className="text-xs">Profile</TabsTrigger>
              <TabsTrigger value="music" className="text-xs">Music</TabsTrigger>
              <TabsTrigger value="compatibility" className="text-xs">Why Match?</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="profile" className="pt-0">
            <ProfileTabContent profile={profile} />
          </TabsContent>
          
          <TabsContent value="music" className="pt-0">
            <MusicTabContent spotifyProfile={profile.spotifyProfile} />
          </TabsContent>
          
          <TabsContent value="compatibility" className="pt-0">
            <CompatibilityTabContent matchScore={profile.matchScore} />
          </TabsContent>
        </Tabs>
        
        <SwipeCardFooter 
          onLike={handleLikeClick}
          onPass={handlePassClick}
          isAnimating={isAnimating}
        />
        
        <div className="absolute inset-0 pointer-events-none">
          <motion.div 
            className="h-full w-full border-4 border-red-500 rounded-lg opacity-0"
            animate={{ opacity: exitDirection === 'left' ? 0.7 : 0 }}
          />
          <motion.div 
            className="h-full w-full border-4 border-green-500 rounded-lg opacity-0"
            animate={{ opacity: exitDirection === 'right' ? 0.7 : 0 }}
          />
        </div>
      </Card>
    </motion.div>
  );
};

export default SwipeCard;
